/**
 * @fileoverview Assist Field Registry - The single source of truth for all imported fields.
 *
 * Each entry maps one imported field to its step, label, severity, and import payload path.
 * The registry drives: panel rows, gap counts, banner summaries, and stepper badges.
 *
 * MVP step scope: case_info, client_info, liability, treatment, coverage.
 */
import type { AssistFieldEntry } from './types';

/**
 * Complete field registry for the MVP assist system.
 * Ordered by step, then by display priority within each step.
 */
export const ASSIST_FIELD_REGISTRY: AssistFieldEntry[] = [
  // =========================================================================
  // BASICS (Step 0 — Firm, Name, Case Type)
  // =========================================================================
  {
    fieldId: 'case_info.firm_name',
    stepId: 'basics',
    label: 'Firm Name',
    severity: 'low',
    importedValuePath: 'case_info.firm_name.value',
    importedPresencePath: 'case_info.firm_name.presence',
  },
  {
    fieldId: 'case_info.case_type',
    stepId: 'basics',
    label: 'Case Type',
    severity: 'medium',
    importedValuePath: 'case_info.case_type.value',
    importedPresencePath: 'case_info.case_type.presence',
  },
  {
    fieldId: 'client_info.first_name',
    stepId: 'basics',
    label: 'First Name',
    severity: 'high',
    importedValuePath: 'client_info.first_name.value',
    importedPresencePath: 'client_info.first_name.presence',
  },
  {
    fieldId: 'client_info.last_name',
    stepId: 'basics',
    label: 'Last Name',
    severity: 'high',
    importedValuePath: 'client_info.last_name.value',
    importedPresencePath: 'client_info.last_name.presence',
  },

  // =========================================================================
  // CASE INFO (Step 1 — Incident Details, Dates, CMS Link)
  // =========================================================================
  {
    fieldId: 'case_info.date_of_loss',
    stepId: 'case_info',
    label: 'Date of Loss',
    severity: 'high',
    importedValuePath: 'case_info.date_of_loss.value',
    importedPresencePath: 'case_info.date_of_loss.presence',
  },
  {
    fieldId: 'case_info.statute_of_limitations_date',
    stepId: 'case_info',
    label: 'Statute of Limitations',
    severity: 'high',
    importedValuePath: 'case_info.statute_of_limitations_date.value',
    importedPresencePath: 'case_info.statute_of_limitations_date.presence',
  },
  {
    fieldId: 'case_info.representing_attorney',
    stepId: 'case_info',
    label: 'Representing Attorney',
    severity: 'medium',
    importedValuePath: 'case_info.representing_attorney.value',
    importedPresencePath: 'case_info.representing_attorney.presence',
  },
  {
    fieldId: 'case_info.incident_location',
    stepId: 'case_info',
    label: 'Incident Location',
    severity: 'medium',
    importedValuePath: 'case_info.incident_location.value',
    importedPresencePath: 'case_info.incident_location.presence',
  },
  {
    fieldId: 'case_info.incident_summary',
    stepId: 'case_info',
    label: 'Incident Summary',
    severity: 'low',
    importedValuePath: 'case_info.incident_summary.value',
    importedPresencePath: 'case_info.incident_summary.presence',
  },
  {
    fieldId: 'case_info.case_id',
    stepId: 'case_info',
    label: 'External Case ID',
    severity: 'low',
    importedValuePath: 'case_info.case_id.value',
    importedPresencePath: 'case_info.case_id.presence',
  },
  {
    fieldId: 'case_info.external_cms_url',
    stepId: 'case_info',
    label: 'CMS Link',
    severity: 'low',
    importedValuePath: 'case_info.external_cms_url.value',
    importedPresencePath: 'case_info.external_cms_url.presence',
  },

  // =========================================================================
  // CLIENT INFO (Step 2 — Contact details, address, DOB, SSN)
  // =========================================================================
  {
    fieldId: 'client_info.email',
    stepId: 'client_info',
    label: 'Email',
    severity: 'medium',
    importedValuePath: 'client_info.email.value',
    importedPresencePath: 'client_info.email.presence',
  },
  {
    fieldId: 'client_info.phone_number',
    stepId: 'client_info',
    label: 'Phone Number',
    severity: 'medium',
    importedValuePath: 'client_info.phone_number.value',
    importedPresencePath: 'client_info.phone_number.presence',
  },
  {
    fieldId: 'client_info.date_of_birth',
    stepId: 'client_info',
    label: 'Date of Birth',
    severity: 'medium',
    importedValuePath: 'client_info.date_of_birth.value',
    importedPresencePath: 'client_info.date_of_birth.presence',
  },
  {
    fieldId: 'client_info.ssn_last_four',
    stepId: 'client_info',
    label: 'SSN (Last 4)',
    severity: 'low',
    importedValuePath: 'client_info.ssn_last_four.value',
    importedPresencePath: 'client_info.ssn_last_four.presence',
  },
  {
    fieldId: 'client_info.address',
    stepId: 'client_info',
    label: 'Address',
    severity: 'medium',
    importedValuePath: 'client_info.address.value',
    importedPresencePath: 'client_info.address.presence',
  },

  // =========================================================================
  // LIABILITY
  // =========================================================================
  {
    fieldId: 'liability.police_report_number',
    stepId: 'liability',
    label: 'Police Report Number',
    severity: 'high',
    importedValuePath: 'liability.police_report_number.value',
    importedPresencePath: 'liability.police_report_number.presence',
  },
  {
    fieldId: 'liability.police_report_date',
    stepId: 'liability',
    label: 'Report Date',
    severity: 'medium',
    importedValuePath: 'liability.police_report_date.value',
    importedPresencePath: 'liability.police_report_date.presence',
  },
  {
    fieldId: 'liability.police_department',
    stepId: 'liability',
    label: 'Police Department',
    severity: 'medium',
    importedValuePath: 'liability.police_department.value',
    importedPresencePath: 'liability.police_department.presence',
  },
  {
    fieldId: 'liability.at_fault_party',
    stepId: 'liability',
    label: 'At-Fault Party',
    severity: 'high',
    importedValuePath: 'liability.at_fault_party.value',
    importedPresencePath: 'liability.at_fault_party.presence',
  },
  {
    fieldId: 'liability.liability_notes',
    stepId: 'liability',
    label: 'Liability Notes',
    severity: 'low',
    importedValuePath: 'liability.liability_notes.value',
    importedPresencePath: 'liability.liability_notes.presence',
  },

  // =========================================================================
  // TREATMENT
  // =========================================================================
  {
    fieldId: 'treatment.providers',
    stepId: 'treatment',
    label: 'Medical Providers',
    severity: 'high',
    importedValuePath: 'treatment.providers.value',
    importedPresencePath: 'treatment.providers.presence',
    isMultiEntity: true,
  },
  {
    fieldId: 'treatment.treatment_notes',
    stepId: 'treatment',
    label: 'Treatment Notes',
    severity: 'low',
    importedValuePath: 'treatment.treatment_notes.value',
    importedPresencePath: 'treatment.treatment_notes.presence',
  },

  // =========================================================================
  // COVERAGE
  // =========================================================================
  {
    fieldId: 'coverage.first_party',
    stepId: 'coverage',
    label: 'First Party Coverage',
    severity: 'high',
    importedValuePath: 'coverage.first_party.value',
    importedPresencePath: 'coverage.first_party.presence',
    isMultiEntity: true,
  },
  {
    fieldId: 'coverage.third_party',
    stepId: 'coverage',
    label: 'Third Party Coverage',
    severity: 'high',
    importedValuePath: 'coverage.third_party.value',
    importedPresencePath: 'coverage.third_party.presence',
    isMultiEntity: true,
  },
  {
    fieldId: 'coverage.health_insurance',
    stepId: 'coverage',
    label: 'Health Insurance',
    severity: 'high',
    importedValuePath: 'coverage.health_insurance.value',
    importedPresencePath: 'coverage.health_insurance.presence',
    isMultiEntity: true,
  },
  {
    fieldId: 'coverage.coverage_notes',
    stepId: 'coverage',
    label: 'Coverage Notes',
    severity: 'low',
    importedValuePath: 'coverage.coverage_notes.value',
    importedPresencePath: 'coverage.coverage_notes.presence',
  },
];

/** Registry entries grouped by step ID for fast lookup */
export const REGISTRY_BY_STEP: Record<string, AssistFieldEntry[]> =
  ASSIST_FIELD_REGISTRY.reduce(
    (acc, entry) => {
      if (!acc[entry.stepId]) acc[entry.stepId] = [];
      acc[entry.stepId].push(entry);
      return acc;
    },
    {} as Record<string, AssistFieldEntry[]>,
  );

